import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { Phone, PhoneOff, Mic, MicOff, PhoneCall, Video, VideoOff, Bell, Check, Play } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { useSocket } from "./SocketContext";
import { useRingtone, RINGTONES, previewRingtone, getSavedRingtone } from "./useRingtone";
import { usePeerConnection } from "./usePeerConnection";
import { useVoicemail } from "./useVoicemail";

// Re-exported for consumers that import ringtone helpers from CallContext.
export { RINGTONES, previewRingtone };

const CallContext = createContext(null);
const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));


export function CallProvider({ children }) {
  const { user } = useAuth();
  const { send, subscribe } = useSocket();
  const [config, setConfig] = useState({ audio_enabled: false, video_enabled: false, ice_servers: [] });
  const [phase, setPhase] = useState("idle"); // idle | calling | incoming | connecting | in-call | voicemail
  const [peer, setPeer] = useState(null); // {id, name, avatar_url}
  const [media, setMedia] = useState("audio");
  const [seconds, setSeconds] = useState(0);

  const callIdRef = useRef(null);
  const peerRef = useRef(null);
  const mediaRef = useRef("audio");
  const ringTimeout = useRef(null);
  const timerRef = useRef(null);
  const phaseRef = useRef("idle");
  phaseRef.current = phase;

  useEffect(() => {
    if (!(user && user !== false)) return;
    const loadConfig = () => api.get("/calls/config").then((r) => setConfig(r.data)).catch(() => {});
    loadConfig();
    const onFocus = () => loadConfig();
    window.addEventListener("focus", onFocus);
    const unsub = subscribe && subscribe((ev) => { if (ev?.type === "call_settings_updated") loadConfig(); });
    return () => { window.removeEventListener("focus", onFocus); if (unsub) unsub(); };
  }, [user, subscribe]);

  // ---- ringtone (focused hook) ----
  const { startRing, stopRing } = useRingtone();

  // ---- WebRTC peer connection (focused hook) ----
  // onRemoteEnd is kept stable via a ref so the peer callbacks (and the signalling
  // effect that depends on them) don't re-create on every render.
  const endCallRef = useRef();
  const onRemoteEnd = useCallback(() => endCallRef.current && endCallRef.current(false), []);
  const {
    pcRef, localRef, audioElRef, localVideoRef, remoteVideoRef,
    muted, camOff, getMic, startOffer, answerOffer, applyAnswer, addIce,
    toggleMute, toggleCamera, stopLocal, closePeer,
  } = usePeerConnection({ iceServers: config.ice_servers, send, callIdRef, mediaRef, phaseRef, onRemoteEnd });

  // ---- voicemail / video message (focused hook) ----
  const cleanupRef = useRef();
  const onVmDone = useCallback(() => cleanupRef.current && cleanupRef.current(), []);
  const {
    recording, recorded, recSecs, sendingVm,
    startRecord, stopRecord, sendVoicemail, resetVoicemail,
  } = useVoicemail({ peerRef, mediaRef, localVideoRef, onSent: onVmDone });

  const cleanup = useCallback(() => {
    stopRing();
    clearTimeout(ringTimeout.current);
    clearInterval(timerRef.current);
    resetVoicemail();
    closePeer();
    callIdRef.current = null; peerRef.current = null; mediaRef.current = "audio";
    setPeer(null); setMedia("audio"); setSeconds(0); setPhase("idle");
  }, [stopRing, resetVoicemail, closePeer]);
  cleanupRef.current = cleanup;

  // After an unanswered call, keep the panel open so the caller can leave a message.
  const toVoicemail = useCallback(() => {
    stopRing();
    clearTimeout(ringTimeout.current);
    clearInterval(timerRef.current);
    stopLocal();
    setPhase("voicemail");
  }, [stopRing, stopLocal]);

  const startInCallTimer = useCallback(() => {
    setPhase("in-call"); setSeconds(0);
    timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
  }, []);

  // ---- caller ----
  const startCall = useCallback(async (target, kind = "audio") => {
    if (kind === "video" ? !config.video_enabled : !config.audio_enabled) return;
    if (phaseRef.current !== "idle") return;
    callIdRef.current = uuid();
    peerRef.current = target; mediaRef.current = kind;
    setPeer(target); setMedia(kind); setPhase("calling");
    startRing(false);
    send({ type: "call_invite", to: target.id, call_id: callIdRef.current, media: kind });
    ringTimeout.current = setTimeout(() => {
      if (phaseRef.current === "calling") {
        send({ type: "call_missed", to: target.id, call_id: callIdRef.current, media: kind });
        toVoicemail();
      }
    }, 30000);
  }, [config.audio_enabled, config.video_enabled, send, startRing, toVoicemail]);

  const acceptCall = useCallback(async () => {
    if (phaseRef.current !== "incoming") return;
    stopRing();
    try { await getMic(mediaRef.current); } catch (e) { declineCall(); return; }
    setPhase("connecting");
    send({ type: "call_accept", to: peerRef.current.id, call_id: callIdRef.current });
  }, [getMic, send, stopRing]); // eslint-disable-line react-hooks/exhaustive-deps

  const declineCall = useCallback(() => {
    if (peerRef.current) send({ type: "call_decline", to: peerRef.current.id, call_id: callIdRef.current });
    cleanup();
  }, [send, cleanup]);

  const endCall = useCallback((notify = true) => {
    if (notify && peerRef.current) {
      if (phaseRef.current === "calling") {
        send({ type: "call_missed", to: peerRef.current.id, call_id: callIdRef.current, media: mediaRef.current });
        toVoicemail();
        return;
      }
      send({ type: "call_end", to: peerRef.current.id, call_id: callIdRef.current });
    }
    cleanup();
  }, [send, cleanup, toVoicemail]);
  endCallRef.current = endCall;

  // ---- signalling ----
  useEffect(() => {
    const unsub = subscribe(async (data) => {
      const t = data.type;
      if (t === "call_incoming") {
        if (phaseRef.current !== "idle") { send({ type: "call_decline", to: data.from.id, call_id: data.call_id }); return; }
        callIdRef.current = data.call_id; peerRef.current = data.from; mediaRef.current = data.media || "audio";
        setPeer(data.from); setMedia(data.media || "audio"); setPhase("incoming"); startRing(true);
      } else if (t === "call_accept") {
        // caller side: peer accepted → create offer
        stopRing();
        try { await getMic(mediaRef.current); } catch (e) { endCall(); return; }
        setPhase("connecting");
        await startOffer(peerRef.current.id);
      } else if (t === "call_signal") {
        const d = data.data || {};
        if (d.kind === "offer") {
          await answerOffer(peerRef.current.id, d.sdp);
          startInCallTimer();
        } else if (d.kind === "answer") {
          await applyAnswer(d.sdp);
          startInCallTimer();
        } else if (d.kind === "ice") {
          await addIce(d.candidate);
        }
      } else if (t === "call_decline") {
        cleanup();
      } else if (t === "call_cancel" || t === "call_end" || t === "call_missed") {
        cleanup();
      }
    });
    return unsub;
  }, [subscribe, send, getMic, startOffer, answerOffer, applyAnswer, addIce, startRing, stopRing, cleanup, endCall, startInCallTimer]);

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const av = (p) => (p?.avatar_url ? `${p.avatar_url}?w=160` : null);
  const isVideo = media === "video";
  const showVideoStage = isVideo && (phase === "in-call" || phase === "connecting");


  return (
    <CallContext.Provider value={{ startCall, callsEnabled: config.audio_enabled, videoEnabled: config.video_enabled, phase }}>
      {children}
      <audio ref={audioElRef} autoPlay playsInline />
      {phase !== "idle" && peer && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/85 backdrop-blur-sm" data-testid="call-overlay">
          <div className={`relative flex flex-col items-center shadow-2xl ${showVideoStage ? "w-full h-full sm:w-[720px] sm:h-[520px] sm:rounded-3xl overflow-hidden bg-black sm:border sm:border-slate-700" : "w-[320px] rounded-3xl border border-slate-700 bg-slate-900 p-7"}`}>
            {/* Video stage */}
            {showVideoStage && (
              <>
                <video ref={remoteVideoRef} autoPlay playsInline className="absolute inset-0 w-full h-full object-cover bg-slate-900" data-testid="call-remote-video" />
                <video ref={localVideoRef} autoPlay playsInline muted className="absolute bottom-24 right-4 w-28 h-40 object-cover rounded-xl border border-slate-600 bg-slate-800 z-10" data-testid="call-local-video" />
                <div className="absolute top-4 left-0 right-0 text-center z-10">
                  <p className="text-white font-semibold drop-shadow">{peer.name}</p>
                  <p className="text-xs text-slate-200 drop-shadow" data-testid="call-status">{phase === "connecting" ? "Connecting…" : fmt(seconds)}</p>
                </div>
              </>
            )}
            {/* Avatar stage (audio, or ringing/incoming) */}
            {!showVideoStage && phase !== "voicemail" && (
              <>
                <div className="w-24 h-24 rounded-full ring-4 ring-sky-500/30 overflow-hidden bg-slate-800 flex items-center justify-center mb-4">
                  {av(peer) ? <img loading="lazy" decoding="async" src={av(peer)} alt={peer.name} className="w-full h-full object-cover" /> : <span className="text-3xl text-slate-400">{(peer.name || "?")[0]}</span>}
                </div>
                <p className="text-lg font-semibold text-slate-100">{peer.name}</p>
                <p className="text-xs text-slate-400 mt-1 mb-6" data-testid="call-status">
                  {phase === "calling" && `Calling…${isVideo ? " (video)" : ""}`}
                  {phase === "incoming" && `Incoming ${isVideo ? "video" : "audio"} call`}
                  {phase === "connecting" && "Connecting…"}
                  {phase === "in-call" && fmt(seconds)}
                </p>
              </>
            )}
            {/* Controls */}
            {phase !== "voicemail" && (
            <div className={`flex items-center gap-5 ${showVideoStage ? "absolute bottom-6 left-0 right-0 justify-center z-10" : ""}`}>
              {phase === "incoming" ? (
                <>
                  <button onClick={declineCall} data-testid="call-decline" className="w-14 h-14 rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center transition-colors"><PhoneOff className="w-6 h-6 text-white" /></button>
                  <button onClick={acceptCall} data-testid="call-accept" className="w-14 h-14 rounded-full bg-emerald-600 hover:bg-emerald-500 flex items-center justify-center transition-colors animate-pulse"><Phone className="w-6 h-6 text-white" /></button>
                </>
              ) : (
                <>
                  {phase === "in-call" && (
                    <button onClick={toggleMute} data-testid="call-mute" className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${muted ? "bg-amber-600 hover:bg-amber-500" : "bg-slate-700 hover:bg-slate-600"}`}>
                      {muted ? <MicOff className="w-5 h-5 text-white" /> : <Mic className="w-5 h-5 text-white" />}
                    </button>
                  )}
                  {phase === "in-call" && isVideo && (
                    <button onClick={toggleCamera} data-testid="call-camera" className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${camOff ? "bg-amber-600 hover:bg-amber-500" : "bg-slate-700 hover:bg-slate-600"}`}>
                      {camOff ? <VideoOff className="w-5 h-5 text-white" /> : <Video className="w-5 h-5 text-white" />}
                    </button>
                  )}
                  <button onClick={() => endCall(true)} data-testid="call-end" className="w-14 h-14 rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center transition-colors"><PhoneOff className="w-6 h-6 text-white" /></button>
                </>
              )}
            </div>
            )}
            {/* Voicemail / video-message panel after an unanswered call */}
            {phase === "voicemail" && (
              <div className="flex flex-col items-center w-full" data-testid="call-voicemail">
                <p className="text-base font-semibold text-slate-100">No answer</p>
                <p className="text-xs text-slate-400 mt-1 mb-4 text-center">Leave {peer.name} a {isVideo ? "video" : "voice"} message?</p>
                {isVideo && recording && <video ref={localVideoRef} autoPlay playsInline muted className="w-40 h-28 object-cover rounded-xl border border-slate-700 bg-slate-800 mb-3" />}
                {recording && <p className="text-xs text-red-400 mb-3 flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> Recording {fmt(recSecs)}</p>}
                {recorded && !recording && <p className="text-xs text-emerald-400 mb-3">Recorded {fmt(recSecs)} — ready to send</p>}
                <div className="flex items-center gap-3">
                  {!recording && !recorded && (
                    <button onClick={startRecord} data-testid="vm-record" className="px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium flex items-center gap-2">
                      {isVideo ? <Video className="w-4 h-4" /> : <Mic className="w-4 h-4" />} Record
                    </button>
                  )}
                  {recording && (
                    <button onClick={stopRecord} data-testid="vm-stop" className="px-4 py-2 rounded-full bg-red-600 hover:bg-red-600 text-white text-sm font-medium">Stop</button>
                  )}
                  {recorded && !recording && (
                    <button onClick={sendVoicemail} disabled={sendingVm} data-testid="vm-send" className="px-4 py-2 rounded-full bg-sky-600 hover:bg-sky-500 text-white text-sm font-medium disabled:opacity-50">{sendingVm ? "Sending…" : "Send message"}</button>
                  )}
                  <button onClick={() => cleanup()} data-testid="vm-skip" className="px-4 py-2 rounded-full bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm">Skip</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </CallContext.Provider>
  );
}

export const useCall = () => useContext(CallContext) || { startCall: () => {}, callsEnabled: false, videoEnabled: false, phase: "idle" };

export function RingtonePicker({ onClose }) {
  const [sel, setSel] = useState(getSavedRingtone());
  const choose = (id) => { setSel(id); localStorage.setItem("ps_ringtone", id); previewRingtone(id); };
  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-slate-950/70 backdrop-blur-sm p-4" data-testid="ringtone-picker" onClick={onClose}>
      <div className="w-[320px] rounded-2xl border border-slate-700 bg-slate-900 p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-1"><Bell className="w-4 h-4 text-amber-300" /><h3 className="text-sm font-semibold text-slate-100">Incoming call ringtone</h3></div>
        <p className="text-xs text-slate-500 mb-4">Tap a tone to preview and set it. Saved on this device.</p>
        <div className="space-y-2">
          {RINGTONES.map((r) => (
            <button key={r.id} onClick={() => choose(r.id)} data-testid={`ringtone-${r.id}`}
              className={`w-full flex items-center justify-between rounded-xl px-4 py-3 border transition-colors ${sel === r.id ? "border-amber-500/60 bg-amber-500/10 text-amber-100" : "border-slate-800 bg-slate-800/40 text-slate-200 hover:bg-slate-800"}`}>
              <span className="text-sm font-medium">{r.name}</span>
              {sel === r.id ? <Check className="w-4 h-4 text-amber-300" /> : <Play className="w-4 h-4 opacity-60" />}
            </button>
          ))}
        </div>
        <button onClick={onClose} data-testid="ringtone-done" className="mt-4 w-full rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold py-2 text-sm">Done</button>
      </div>
    </div>
  );
}

export { PhoneCall };
